import { useState, useEffect } from 'react'
import { api, type FocusTask } from '../lib/api'
import { hapticFeedback, hapticNotification } from '../lib/telegram'
import { SubtasksList } from './SubtasksList'

interface FocusViewProps {
  category: 'work' | 'personal'
  onTaskComplete: () => void
}

export function FocusView({ category, onTaskComplete }: FocusViewProps) {
  const [focusTask, setFocusTask] = useState<FocusTask | null>(null)
  const [loading, setLoading] = useState(true)
  const [completing, setCompleting] = useState(false)
  const [celebrating, setCelebrating] = useState(false)
  const [skippedIds, setSkippedIds] = useState<string[]>([])
  const [showCompletePrompt, setShowCompletePrompt] = useState(false)

  const fetchFocusTask = async (exclude: string[] = []) => {
    try {
      setLoading(true)
      const task = await api.getFocusTask(category, exclude)
      if (!task && exclude.length > 0) {
        // All tasks skipped — loop back from the start
        setSkippedIds([])
        const freshTask = await api.getFocusTask(category, [])
        setFocusTask(freshTask)
      } else {
        setFocusTask(task)
      }
    } catch (e) {
      console.error('Failed to fetch focus task:', e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setSkippedIds([])
    setShowCompletePrompt(false)
    fetchFocusTask([])
  }, [category])

  const handleSubtaskUpdate = async () => {
    if (!focusTask) return
    
    // Refetch the task to get updated subtask counts
    try {
      const updatedTask = await api.getFocusTask(category, [])
      if (updatedTask?.id === focusTask.id) {
        setFocusTask(updatedTask)
        
        // Check if all subtasks are now complete
        const subtasks = updatedTask.subtasks || []
        const allSubtasksComplete = subtasks.length > 0 && 
          subtasks.every(s => s.completed)
        
        if (allSubtasksComplete && !showCompletePrompt) {
          setShowCompletePrompt(true)
          hapticNotification('success')
        }
      }
    } catch (e) {
      console.error('Failed to refresh focus task:', e)
    }
  }

  const handleCompleteNextStep = async () => {
    if (!focusTask || completing) return
    
    // Find the next uncompleted subtask
    const subtasks = focusTask.subtasks || []
    const nextSubtask = subtasks.find(s => !s.completed)
    
    if (nextSubtask) {
      // Complete the next subtask
      try {
        setCompleting(true)
        hapticNotification('success')
        await api.updateSubtask(nextSubtask.id, { completed: true })
        setCompleting(false)
        await handleSubtaskUpdate()
      } catch (e) {
        console.error('Failed to complete subtask:', e)
        setCompleting(false)
      }
    } else {
      // No subtasks or all done — complete the whole task
      await handleCompleteTask()
    }
  }

  const handleCompleteTask = async () => {
    if (!focusTask || completing) return
    
    try {
      setCompleting(true)
      hapticNotification('success')
      
      await api.updateTask(focusTask.id, { completed: true })
      
      // Celebration animation
      setCelebrating(true)
      setShowCompletePrompt(false)
      setTimeout(() => {
        setCelebrating(false)
        setCompleting(false)
        onTaskComplete()
        const newSkipped = skippedIds.filter(id => id !== focusTask.id)
        setSkippedIds(newSkipped)
        fetchFocusTask(newSkipped)
      }, 1500)
      
    } catch (e) {
      console.error('Failed to complete task:', e)
      setCompleting(false)
    }
  }

  const handleSkip = () => {
    if (!focusTask) return
    hapticFeedback('light')
    const newSkipped = [...skippedIds, focusTask.id]
    setSkippedIds(newSkipped)
    fetchFocusTask(newSkipped)
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'border-red-500'
      case 'this_week': return 'border-yellow-500'
      case 'normal': return 'border-blue-500'
      case 'backlog': return 'border-gray-500'
      default: return 'border-gray-500'
    }
  }

  const getPriorityBg = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500/10'
      case 'this_week': return 'bg-yellow-500/10'
      case 'normal': return 'bg-blue-500/10'
      case 'backlog': return 'bg-gray-500/10'
      default: return 'bg-gray-500/10'
    }
  }

  const getTimeEstimateDisplay = (timeEstimate: string | null) => {
    if (!timeEstimate) return null
    const estimates: Record<string, string> = {
      '15min': '15m',
      '1hr': '1h',
      'half_day': '4h',
      'full_day': '8h'
    }
    return estimates[timeEstimate] || timeEstimate
  }

  const getDaysLeft = (dueDate: string | null) => {
    if (!dueDate) return null
    const due = new Date(dueDate)
    const now = new Date()
    const diffTime = due.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays < 0) return 'Overdue'
    if (diffDays === 0) return 'Due today'
    if (diffDays === 1) return '1 day left'
    return `${diffDays} days left`
  }

  if (loading) {
    return (
      <div className="px-4 py-8">
        <div className="max-w-md mx-auto text-center">
          <div className="text-4xl mb-4">🎯</div>
          <div className="spinner" />
          <p className="text-sm text-muted mt-3">Finding your next focus...</p>
        </div>
      </div>
    )
  }

  if (!focusTask) {
    return (
      <div className="px-4 py-8">
        <div className="max-w-md mx-auto text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-xl font-bold mb-2">All caught up!</h2>
          <p className="text-sm text-muted">
            No {category} tasks need your attention right now. Great job!
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 py-6">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="text-4xl mb-2">🎯</div>
          <h1 className="text-xl font-bold">Focus Now</h1>
          <p className="text-sm text-muted">One thing at a time</p>
        </div>

        {/* Focus Card */}
        <div className={`
          glass rounded-2xl p-6 border-l-4 ${getPriorityColor(focusTask.priority)} 
          ${getPriorityBg(focusTask.priority)} mb-6 relative overflow-hidden
          ${celebrating ? 'animate-pulse' : ''}
        `}>
          {celebrating && (
            <div className="absolute inset-0 bg-gradient-to-r from-green-500/20 to-blue-500/20 animate-pulse" />
          )}
          
          <div className="relative">
            {/* Priority & Time */}
            <div className="flex items-center gap-2 mb-3">
              <span className={`
                px-2 py-1 rounded-full text-xs font-medium uppercase tracking-wide
                ${focusTask.priority === 'urgent' ? 'bg-red-500/20 text-red-300' :
                  focusTask.priority === 'this_week' ? 'bg-yellow-500/20 text-yellow-300' :
                  focusTask.priority === 'normal' ? 'bg-blue-500/20 text-blue-300' :
                  'bg-gray-500/20 text-gray-300'}
              `}>
                {focusTask.priority.replace('_', ' ')}
              </span>
              {focusTask.time_estimate && (
                <span className="px-2 py-1 rounded-full text-xs bg-white/10 text-white/70">
                  {getTimeEstimateDisplay(focusTask.time_estimate)}
                </span>
              )}
            </div>

            {/* Title */}
            <h2 className="text-lg font-bold mb-2 leading-tight">
              {focusTask.title}
            </h2>

            {/* Project */}
            {focusTask.project_name && (
              <p className="text-sm text-muted mb-2">
                {focusTask.project_emoji} {focusTask.project_name}
              </p>
            )}

            {/* Assigned to */}
            {focusTask.assigned_to && (
              <p className="text-sm text-white/70 mb-2">
                👤 Assigned to {focusTask.assigned_to}
              </p>
            )}

            {/* Due date */}
            {focusTask.due_date && (
              <p className={`text-sm font-medium ${
                getDaysLeft(focusTask.due_date) === 'Overdue' ? 'text-red-300' :
                getDaysLeft(focusTask.due_date) === 'Due today' ? 'text-yellow-300' :
                'text-white/70'
              }`}>
                📅 {getDaysLeft(focusTask.due_date)}
              </p>
            )}
          </div>
        </div>

        {/* Subtasks */}
        {focusTask.subtasks && focusTask.subtasks.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-white/80 mb-3 flex items-center gap-2">
              <span>📋</span>
              Steps to complete
            </h3>
            <div className="glass rounded-xl p-4">
              <SubtasksList
                taskId={focusTask.id}
                subtasks={focusTask.subtasks}
                onSubtaskUpdate={handleSubtaskUpdate}
                showProgress={true}
                allowAdd={false}
              />
            </div>
          </div>
        )}

        {/* Complete prompt */}
        {showCompletePrompt && (
          <div className="glass rounded-xl p-4 mb-6 border border-green-500/30 bg-green-500/10">
            <div className="text-center">
              <div className="text-2xl mb-2">🎉</div>
              <h3 className="font-medium text-green-300 mb-1">All steps complete!</h3>
              <p className="text-sm text-green-200/80">Ready to mark this task as done?</p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          {/* Main button: complete next step OR complete task if all done / no subtasks */}
          {(() => {
            const subtasks = focusTask.subtasks || []
            const nextSubtask = subtasks.find(s => !s.completed)
            const allDone = subtasks.length > 0 && !nextSubtask
            
            return (
              <>
                <button
                  onClick={allDone ? handleCompleteTask : handleCompleteNextStep}
                  disabled={completing || celebrating}
                  className={`
                    w-full py-4 rounded-xl font-medium text-lg transition-all active:scale-95
                    ${completing || celebrating 
                      ? 'bg-green-500/50 text-white cursor-not-allowed' 
                      : allDone 
                        ? 'bg-green-500 text-white hover:bg-green-600'
                        : 'bg-green-500 text-white hover:bg-green-600'
                    }
                  `}
                >
                  {celebrating ? '🎉 Awesome!' : completing ? 'Completing...' : 
                   allDone ? '🏁 Complete Task' :
                   nextSubtask ? `✅ ${nextSubtask.title}` : '✅ Done'}
                </button>
                
                {/* Show "Complete Entire Task" as secondary when there are still subtasks left */}
                {nextSubtask && !completing && !celebrating && (
                  <button
                    onClick={handleCompleteTask}
                    className="w-full py-2.5 rounded-xl font-medium text-xs bg-green-500/10 text-green-300/80 hover:bg-green-500/20 transition-all active:scale-95"
                  >
                    🏁 Complete entire task
                  </button>
                )}
              </>
            )
          })()}
          
          <button
            onClick={handleSkip}
            disabled={completing || celebrating}
            className="w-full py-3 rounded-xl font-medium bg-white/10 text-white/80 hover:bg-white/20 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ⏭️ Skip for now
          </button>
        </div>

        {celebrating && (
          <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-50">
            <div className="text-8xl animate-bounce">🎉</div>
          </div>
        )}
      </div>
    </div>
  )
}